import { useRouter } from 'next/navigation';
import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/store';
import { ROUTES } from '@/config/routes';
import { useCurrentUser } from '@/redux/selectors/userSelector';

const allowedRoles = ['admin', 'tech', 'ops'];
export const canAccessAdmin = (role: string | null) => {
  if (!role) {
    return false;
  }
  return allowedRoles.includes(role);
};

const withAdminAuthorization = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  const WithAuthorization = (props: P) => {
    const router = useRouter();
    const user = useSelector((state: RootState) => state.user);

    if (user.status !== 'succeeded' && user.status !== 'failed') {
      return null;
    }

    if (!canAccessAdmin(user.role)) {
      router.push(ROUTES.UNAUTHORIZED);
      return null;
    }

    return <WrappedComponent {...props} />;
  };

  WithAuthorization.displayName = `withAuthorization(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithAuthorization;
};

export const useCurrentUserPermissions = () => {
  const userData = useCurrentUser();
  const hasEapAccess = !!userData.organization?.hasEapAccess;
  const hasOhsAccess = !!userData.organization?.hasOhsHubAccess;
  const hasInstructionsAccess = !!userData.organization?.hasInstructionsAccess;

  const { role } = userData;

  return {
    canEAP: hasEapAccess,
    canOHS: hasOhsAccess,
    canInstructions: hasInstructionsAccess,
    canAdmin: canAccessAdmin(role),
  };
};

export default withAdminAuthorization;
